/**
 * LNV Wishlist Header
 * Version: 1.2
 *
 * Purpose:
 * - Mount the LNV wishlist action inside Horizon's native <header-actions>
 *   immediately before the cart action.
 * - Keep Liquid integration outside the native Web Component markup.
 * - Display browser-local wishlist count from lnv:wishlist:v1.
 * - Remain idempotent during Horizon/Shopify header hydration.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'lnv:wishlist:v1';
  const SELECTOR = '[data-lnv-header-wishlist]';

  let scheduled = false;

  function readCount() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return 0;

      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch (error) {
      return 0;
    }
  }

  function updateLink(link, count) {
    const bubble = link.querySelector('[data-lnv-header-wishlist-count]');

    if (bubble) {
      if (count > 0) {
        bubble.hidden = false;
        bubble.textContent = count > 99 ? '99+' : String(count);
      } else {
        bubble.hidden = true;
        bubble.textContent = '';
      }
    }

    link.setAttribute(
      'aria-label',
      count === 0
        ? 'Wishlist, no saved items'
        : count === 1
          ? 'Wishlist, 1 saved item'
          : `Wishlist, ${count} saved items`
    );
  }

  function findHeaderActions(link) {
    const directParent = link.parentElement;

    if (directParent && directParent.tagName.toLowerCase() === 'header-actions') {
      return directParent;
    }

    if (directParent instanceof HTMLElement) {
      const siblingHost = Array.from(directParent.children).find(
        (element) =>
          element.tagName &&
          element.tagName.toLowerCase() === 'header-actions'
      );

      if (siblingHost) return siblingHost;
    }

    return (
      link.closest('.header__column--right')?.querySelector('header-actions') ||
      null
    );
  }

  function getInsertionPoint(host) {
    return (
      host.querySelector('[data-testid="cart-drawer-trigger"]') ||
      host.querySelector('a.action__cart') ||
      host.querySelector('[ref="liveRegion"]')
    );
  }

  function mountLink(link) {
    const host = findHeaderActions(link);
    if (!host) return false;

    const insertionPoint = getInsertionPoint(host);
    if (!insertionPoint) return false;

    const alreadyCorrect =
      link.parentElement === host &&
      link.nextElementSibling === insertionPoint;

    if (!alreadyCorrect) {
      host.insertBefore(link, insertionPoint);
    }

    link.hidden = false;
    link.dataset.lnvWishlistMounted = 'true';

    return true;
  }

  function removeDuplicateLinks() {
    const links = Array.from(document.querySelectorAll(SELECTOR));

    if (links.length <= 1) return links;

    const preferred =
      links.find(
        (link) => link.dataset.lnvWishlistMounted === 'true'
      ) || links[0];

    links.forEach((link) => {
      if (link !== preferred) {
        link.remove();
      }
    });

    return [preferred];
  }

  function sync() {
    const count = readCount();
    const links = removeDuplicateLinks();

    links.forEach((link) => {
      if (!(link instanceof HTMLElement)) return;

      mountLink(link);
      updateLink(link, count);
    });
  }

  function scheduleSync() {
    if (scheduled) return;

    scheduled = true;

    window.requestAnimationFrame(() => {
      scheduled = false;
      sync();
    });
  }

  function observeHeader() {
    const root =
      document.getElementById('header-group') ||
      document.body;

    if (!root) return;

    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof Element)) return false;

          return (
            node.matches?.(SELECTOR) ||
            node.matches?.('header-actions') ||
            node.querySelector?.(SELECTOR) ||
            node.querySelector?.('header-actions')
          );
        })
      );

      if (relevant) scheduleSync();
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
    });
  }

  function init() {
    sync();
    observeHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }

  window.addEventListener(
    'lnv:wishlist:changed',
    scheduleSync
  );

  window.addEventListener(
    'storage',
    (event) => {
      if (event.key === STORAGE_KEY) {
        scheduleSync();
      }
    }
  );

  document.addEventListener(
    'shopify:section:load',
    scheduleSync
  );
})();